import { useState, useEffect, useCallback } from 'react';
import type { JobLog, JobLogCreateRequest } from '@techgitter/contracts';
import { getJobLogs, createJobLog, type JobLogParent } from '../services/jobLogService';

export function useJobLogs(
  guid: string | undefined,
  userName?: string,
  parent: JobLogParent = 'job',
) {
  const [logs, setLogs] = useState<JobLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!guid) return;

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    getJobLogs(guid, controller.signal, parent)
      .then(data => setLogs(data))
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load job logs');
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [guid, parent]);

  /** Resolves true once the entry is saved, false when it failed (the error
   *  is in `error`) — so a caller can keep the user's text on failure. */
  const addLog = useCallback(
    async (req: Omit<JobLogCreateRequest, 'jobGUID'>): Promise<boolean> => {
      if (!guid) return false;
      setIsSubmitting(true);
      setError(null);
      try {
        const created = await createJobLog(guid, req, undefined, parent);
        if (!created.createdBy && userName) {
          created.createdBy = userName;
        }
        setLogs(prev => [...prev, created]);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add log entry');
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [guid, parent, userName],
  );

  return { logs, isLoading, error, isSubmitting, addLog };
}
